
import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from "@/components/ui/progress";
import { useTranslation } from 'react-i18next';
import { FeatureGuard } from '@/components/common/FeatureGuard';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  AlertCircle, 
  ExternalLink, 
  FileSpreadsheet, 
  FileText, 
  FileType, 
  Info, 
  FileBarChart2,
  Code,
  MessageSquare,
  Copy,
  Image,
  AlignJustify
} from 'lucide-react';
import { SUPABASE_URL } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';

const DocumentExtractionPage = () => {
  const [file, setFile] = useState<File | null>(null);
  const [extractedData, setExtractedData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [isQuotaError, setIsQuotaError] = useState(false);
  const [extractionMethod, setExtractionMethod] = useState<'vision' | 'text-based' | null>(null);
  const [preferredMethod, setPreferredMethod] = useState<'vision' | 'text'>('text');
  const [modelUsed, setModelUsed] = useState<string | null>(null);
  const [apiRequest, setApiRequest] = useState<string | null>(null);
  const [apiResponse, setApiResponse] = useState<string | null>(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  
  const { user } = useAuth();
  const { t } = useTranslation();
  
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      setExtractedData(null); // Clear previous data
      setError(null); // Clear previous errors
      setErrorDetails(null);
      setIsQuotaError(false);
      setExtractionMethod(null);
      setModelUsed(null);
    }
  }, []);
  
  const {getRootProps, getInputProps, isDragActive} = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp']
    },
    maxFiles: 1
  });
  
  const uploadDocument = async (file: File) => {
    if (!file || !user) return;
    
    setIsUploading(true);
    setUploadProgress(0);
    setError(null);
    setErrorDetails(null);
    setIsQuotaError(false);
    setExtractionMethod(null);
    setModelUsed(null);
    setApiRequest(null);
    setApiResponse(null);
    
    try {
      console.log("Starting document upload process...");
      
      // Simulate progressive upload
      const uploadInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(uploadInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 300);
      
      // Upload the file to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${user.id}/documents/${fileName}`;
      
      console.log(`Uploading to storage path: ${filePath}`);
      const { data, error } = await supabase.storage
        .from('documents')
        .upload(filePath, file, {
          cacheControl: '3600',
          contentType: file.type,
          upsert: false
        });
      
      if (error) {
        clearInterval(uploadInterval);
        console.error("Storage upload error:", error);
        throw new Error(t('failedToUploadDocument', 'Failed to upload document: ') + error.message);
      }
      
      // Create a signed URL for the uploaded file
      const { data: urlData, error: urlError } = await supabase.storage
        .from('documents')
        .createSignedUrl(filePath, 60); // 60 seconds expiry
      
      if (urlError) {
        clearInterval(uploadInterval);
        console.error("Error creating signed URL:", urlError);
        throw new Error(t('failedToProcessDocument', 'Failed to process document: ') + urlError.message);
      }
      
      const documentUrl = urlData.signedUrl;
      console.log("Document signed URL created:", documentUrl);
      
      setUploadProgress(95); // Almost done, waiting for processing
      clearInterval(uploadInterval);
      
      // Process the document with the edge function
      const requestBody = JSON.stringify({
        documentUrl,
        preferredMethod
      });
      
      // Store the API request for debugging
      setApiRequest(requestBody);
      
      const edgeFunctionUrl = `${SUPABASE_URL}/functions/v1/process-document`;
      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: requestBody,
      });
      
      const responseData = await response.json();
      
      // Store the API response for debugging
      setApiResponse(JSON.stringify(responseData, null, 2));
      
      if (!response.ok) {
        let errorMessage = responseData.error || `${t('failedToProcessDocument', 'Failed to process document')}: ${response.status}`;
        
        // Special handling for OpenAI quota errors
        if (response.status === 429) {
          setIsQuotaError(true);
          errorMessage = t('openaiQuotaExceeded', 'AI processing quota exceeded. Please try again later.');
        }
        
        throw new Error(errorMessage);
      }
      
      if (responseData.contractData) {
        setExtractedData(responseData.contractData);
        if (responseData.method) {
          setExtractionMethod(responseData.method);
        }
        if (responseData.usedModel) {
          setModelUsed(responseData.usedModel);
        }
        if (responseData.response) {
          setApiResponse(responseData.response);
        }
        if (responseData.request) {
          setApiRequest(responseData.request);
        }
      } else {
        setExtractedData(responseData);
      }
      
      setUploadProgress(100);
      toast.success(t('documentProcessedSuccessfully', 'Document processed successfully!'));
    } catch (error: any) {
      console.error("Error during document processing:", error);
      
      // Try to extract detailed error information if available
      let errorMsg = error.message || t('failedToProcessDocument', 'Failed to process document. Please try again.');
      let details = null;
      
      // Check if the error response has more details
      if (error.details) {
        details = typeof error.details === 'string' ? error.details : JSON.stringify(error.details, null, 2);
      }
      
      setError(errorMsg);
      setErrorDetails(details);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
      setIsUploading(false);
    }
  };
  
  const handleSubmit = async () => {
    if (!file) {
      toast.error(t('pleaseSelectADocument', 'Please select a document first.'));
      return;
    }
    
    setIsLoading(true);
    await uploadDocument(file);
  };

  const handleRetry = () => {
    setError(null);
    setErrorDetails(null);
    setUploadProgress(0);
    setIsQuotaError(false);
    if (file) {
      setIsLoading(true);
      uploadDocument(file);
    }
  };

  const toggleExtractionMethod = (checked: boolean) => {
    setPreferredMethod(checked ? 'vision' : 'text');
  };
  
  // Function to get the appropriate file icon based on mimetype or extension
  const getFileIcon = (file: File | null) => {
    if (!file) return <FileType className="h-6 w-6" />;
    
    const fileType = file.type.toLowerCase();
    const fileName = file.name.toLowerCase();
    
    if (fileType.includes('spreadsheet') || fileType.includes('excel') || 
        fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
      return <FileBarChart2 className="h-6 w-6 text-green-600" />;
    } else if (fileType.includes('word') || fileType.includes('document') || 
              fileName.endsWith('.doc') || fileName.endsWith('.docx')) {
      return <FileText className="h-6 w-6 text-blue-600" />;
    } else if (fileType.includes('pdf') || fileName.endsWith('.pdf')) {
      return <FileText className="h-6 w-6 text-red-600" />;
    } else if (fileType.includes('image') || 
              fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') || 
              fileName.endsWith('.png') || fileName.endsWith('.gif') || 
              fileName.endsWith('.webp')) {
      return <Image className="h-6 w-6 text-purple-600" />;
    } else {
      return <FileType className="h-6 w-6 text-gray-600" />;
    }
  };
  
  // Function to render field value based on type
  const renderFieldValue = (key: string, value: any) => {
    // Special handling for nested objects (like paymentFrequency)
    if (typeof value === 'object' && value !== null) {
      return (
        <div className="pl-4 border-l-2 border-gray-200">
          {Object.entries(value).map(([nestedKey, nestedValue]: [string, any]) => (
            <div key={`${key}-${nestedKey}`} className="grid grid-cols-2 gap-2 mb-2">
              <span className="text-sm text-muted-foreground">{nestedKey}:</span>
              <span>{String(nestedValue)}</span>
            </div>
          ))}
        </div>
      );
    }
    
    // General display for text fields
    return (
      <div className="mt-1">
        {key === 'description' ? (
          <Textarea 
            value={value || ''} 
            readOnly 
            className="min-h-[100px]"
          />
        ) : (
          <Input value={value || ''} readOnly />
        )}
      </div>
    );
  };
  
  // Organize fields into sections for better readability
  const getFieldSections = () => {
    if (!extractedData) return [];
    
    const sections = [
      {
        title: t('partyInformation', 'Party Information'),
        fields: ['supplierName', 'organisationnumber', 'customerName', 'customerOrganisationnumber']
      },
      {
        title: t('contractDetails', 'Contract Details'),
        fields: ['contractType', 'importance', 'description']
      },
      {
        title: t('financialInformation', 'Financial Information'),
        fields: ['expectedCost', 'paymentFrequency']
      },
      {
        title: t('dates', 'Important Dates'),
        fields: ['signedDate', 'expiryDate']
      }
    ];
    
    return sections.map(section => ({
      ...section,
      // Filter to only include fields that exist in the extracted data
      fields: section.fields.filter(field => field in extractedData)
    })).filter(section => section.fields.length > 0);
  };
  
  // Function to copy content to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        toast.success(t('copiedToClipboard', 'Copied to clipboard'));
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
        toast.error(t('failedToCopy', 'Failed to copy to clipboard'));
      });
  };
  
  return (
    <FeatureGuard feature="document_extraction">
      <div className="container mx-auto p-4">
        <Card>
          <CardHeader>
            <CardTitle>{t('documentExtraction', 'Document Extraction')}</CardTitle>
            <CardDescription>
              {t('extractDataFromDocuments', 'Extract data from documents using AI.')}
              <span className="block text-sm mt-1 text-muted-foreground">
                {t('supportedLanguages', 'Supports multiple languages including English, Norwegian, and Swedish')}
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            {error && (
              <Alert variant={isQuotaError ? "warning" : "destructive"}>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{isQuotaError ? t('quotaExceeded', 'API Quota Exceeded') : t('error', 'Error')}</AlertTitle>
                <AlertDescription className="space-y-2">
                  <p>{error}</p>
                  
                  {isQuotaError && (
                    <div className="mt-2 text-sm">
                      <p>{t('quotaExceededExplanation', 'This happens when the OpenAI API usage limit has been reached. You may need to:')}</p>
                      <ul className="list-disc pl-5 mt-1 space-y-1">
                        <li>{t('quotaWait', 'Wait a while before trying again')}</li>
                        <li>{t('quotaCheckBilling', 'Check the OpenAI account billing status')}</li>
                      </ul>
                      <div className="mt-2">
                        <a 
                          href="https://platform.openai.com/account/billing/overview" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-primary hover:underline"
                        >
                          {t('openaiAccountManagement', 'OpenAI Account Management')}
                          <ExternalLink className="ml-1 h-3 w-3" />
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {errorDetails && !isQuotaError && (
                    <details className="mt-2 text-xs">
                      <summary>{t('showDetails', 'Show technical details')}</summary>
                      <pre className="mt-2 whitespace-pre-wrap p-2 bg-muted rounded-md overflow-auto max-h-[200px]">
                        {errorDetails}
                      </pre>
                    </details>
                  )}
                  
                  <div className="mt-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleRetry}
                      disabled={isLoading || isUploading}
                    >
                      {t('retry', 'Retry')}
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}
            
            <div className="flex items-center space-x-2">
              <div className="grid gap-2">
                <Label htmlFor="extraction-method">
                  {t('extractionMethod', 'Extraction Method')}
                </Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <AlignJustify className="h-4 w-4" />
                    {t('textBased', 'Text-based')}
                  </span>
                  <Switch 
                    id="extraction-method" 
                    checked={preferredMethod === 'vision'}
                    onCheckedChange={toggleExtractionMethod} 
                  />
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <Image className="h-4 w-4" />
                    {t('visionBased', 'Vision-based')}
                  </span>
                </div>
              </div>
              <div className="ml-auto">
                <div className="text-xs text-muted-foreground">
                  {preferredMethod === 'vision' 
                    ? t('visionDescription', 'Best for images and visually complex documents')
                    : t('textDescription', 'Best for text-based documents and larger files')}
                </div>
              </div>
            </div>
            
            <div {...getRootProps()} className={`relative border-2 border-dashed rounded-md p-6 cursor-pointer ${isDragActive ? 'border-primary' : 'border-muted-foreground'}`}>
              <input {...getInputProps()} />
              <div className="text-center">
                <p className="text-muted-foreground">
                  {isDragActive
                    ? t('dropTheFileHere', 'Drop the file here')
                    : `${t('dragAndDropFiles', 'Drag and drop documents here, or click to select files')}`}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  {preferredMethod === 'vision' 
                    ? t('supportedFormatsVision', 'Supported formats: PDF, Images (JPEG, PNG, GIF, WEBP), DOC, DOCX, XLS, XLSX')
                    : t('supportedFormatsText', 'Supported formats: PDF, TXT, DOC, DOCX, XLS, XLSX')}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t('largeDocumentWarning', 'Large documents will be truncated for faster processing')}
                </p>
                {file && (
                  <div className="mt-4 flex items-center justify-center gap-2">
                    {getFileIcon(file)}
                    <p className="text-sm text-muted-foreground">
                      {t('selectedFile', 'Selected file')}: {file.name}
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            {isUploading && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>
                    {uploadProgress < 90 
                      ? t('uploading', 'Uploading') 
                      : (uploadProgress < 100 ? t('processing', 'Processing') : t('complete', 'Complete'))}...
                  </span>
                  <span>{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} className="h-2" />
              </div>
            )}
            
            <div className="flex flex-wrap gap-2">
              <Button 
                onClick={handleSubmit} 
                disabled={!file || isLoading || isUploading}
                className="w-full md:w-auto"
              >
                {isLoading ? `${t('processing', 'Processing')}...` : t('processDocument', 'Process Document')}
              </Button>
              
              {apiRequest && (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex gap-1 items-center"
                  onClick={() => setShowRequestModal(true)}
                >
                  <Code className="h-4 w-4" />
                  {t('viewRequest', 'View Request')}
                </Button>
              )}
              
              {apiResponse && (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex gap-1 items-center"
                  onClick={() => setShowResponseModal(true)}
                >
                  <MessageSquare className="h-4 w-4" />
                  {t('viewResponse', 'View Response')}
                </Button>
              )}
            </div>
            
            {extractedData && (
              <div className="grid gap-6 mt-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">{t('extractedData', 'Extracted Data')}</h2>
                  <div className="flex items-center gap-2">
                    {extractionMethod && (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full flex items-center gap-1">
                        <Info className="h-3 w-3" />
                        {extractionMethod === 'vision' 
                          ? t('visionProcessing', 'Visual processing') 
                          : t('textProcessing', 'Text processing')}
                      </span>
                    )}
                    {modelUsed && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                        {modelUsed}
                      </span>
                    )}
                  </div>
                </div>
                
                {getFieldSections().map((section) => (
                  <Card key={section.title} className="overflow-hidden">
                    <CardHeader className="bg-muted/50 py-3">
                      <CardTitle className="text-md">{section.title}</CardTitle>
                    </CardHeader>
                    <CardContent className="divide-y">
                      {section.fields.map((key) => (
                        <div key={key} className="py-3 first:pt-4 last:pb-4">
                          <Label htmlFor={key} className="font-medium">
                            {t(key, key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'))}
                          </Label>
                          {renderFieldValue(key, extractedData[key])}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* API Request Modal */}
        <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
            <DialogHeader>
              <DialogTitle>{t('apiRequest', 'API Request')}</DialogTitle>
              <DialogDescription>
                {t('requestDescription', 'The request sent to the OpenAI API for processing.')}
              </DialogDescription>
            </DialogHeader>
            <div className="relative">
              <pre className="p-4 bg-muted rounded-md overflow-auto max-h-[60vh] text-xs whitespace-pre-wrap">
                {apiRequest}
              </pre>
              <Button 
                size="sm" 
                variant="ghost" 
                className="absolute top-2 right-2"
                onClick={() => apiRequest && copyToClipboard(apiRequest)}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* API Response Modal */}
        <Dialog open={showResponseModal} onOpenChange={setShowResponseModal}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
            <DialogHeader>
              <DialogTitle>{t('apiResponse', 'API Response')}</DialogTitle>
              <DialogDescription>
                {t('responseDescription', 'The raw response received from the OpenAI API.')}
              </DialogDescription>
            </DialogHeader>
            <div className="relative">
              <pre className="p-4 bg-muted rounded-md overflow-auto max-h-[60vh] text-xs whitespace-pre-wrap">
                {apiResponse}
              </pre>
              <Button 
                size="sm" 
                variant="ghost" 
                className="absolute top-2 right-2"
                onClick={() => apiResponse && copyToClipboard(apiResponse)}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </FeatureGuard>
  );
};

export default DocumentExtractionPage;
